import * as React from 'react';
import { SolutionOutlined, CopyOutlined, FileDoneOutlined, CreditCardOutlined } from '@ant-design/icons';
import { localizedString } from '@locale/index';
import * as Loadable from 'react-loadable';
import { Route } from 'react-router-dom';

export interface MenuDataInfo {
  name: string;
  path: string;
  icon?: JSX.Element | string;
  open?: boolean;
  authority?: string;
  component?: any;
  children?: MenuDataInfo[];
  hidden?: boolean;
  exact?: boolean;
  isLoading?: boolean;
}

export function getRoutes(
  data: MenuDataInfo,
  path: string,
  routes: JSX.Element[],
  isExact?: boolean
) {
  const currentPath = `${path !== undefined ? path + '/' : ''}${data.path}`;

  if (data.component) {
    const Component = Loadable({
      loader: data.component as any,
      loading: () => <div />
    });

    routes.push(
      <Route
        key={currentPath}
        path={currentPath}
        exact={true}
        render={(props: any) => <Component {...props} />}
      />
    );
  }

  if (data.children) {
    data.children.forEach((child: MenuDataInfo) => {
      getRoutes(child, currentPath, routes);
    });
  }
}

const agentChildren: MenuDataInfo[] = [
  {
    name: localizedString('agent_create'),
    path: 'create',
    hidden: true,
    component: () => import('@page/agent/create')
  },
  {
    name: localizedString('agent_create'),
    path: 'clone',
    hidden: true,
    component: () => import('@page/agent/create')
  },
  {
    name: localizedString('agent_detail'),
    path: ':agentId/:language',
    component: () => import('@page/agent/detail'),
    children: [
      {
        name: localizedString('readme'),
        path: 'readme',
        icon: <i className="iconfont icon-Intents_ic" />,
        component: () => import('@page/agent/readme')
      },
      {
        name: localizedString('agent_libraries'),
        path: 'library',
        component: () => import('@page/agent/library')
      },
      {
        name: localizedString('sidebar_service'),
        path: 'service_schema',
        icon: <i className="iconfont icon-shuxing_ic" />,
        component: () => import('@page/service/detail'),
        children: [
          {
            name: localizedString('composite_attribute_detail'),
            path: 'slot/:slotId',
            component: () =>
              import('@page/action-parameter/service-action-parameter')
          },
          {
            name: 'function',
            path: 'function/:functionId',
            component: () =>
              import('@component/data-query/function-detail')
          }
        ]
      },
      {
        name: localizedString('provider_connection'),
        path: 'provider_connection',
        icon: <i className="iconfont icon-shuxing_ic" />,
        component: () => import('@page/service/detail')
      },
      {
        name: localizedString('fake_types'),
        path: 'type',
        icon: <i className="iconfont icon-Intents_ic" />,
        component: () => import('@page/fake-type'),
      },
      {
        name: localizedString('sidebar_intent'),
        path: 'intent',
        icon: <i className="iconfont icon-Intents_ic" />,
        component: () => import('@page/intent'),
        children: [
          {
            name: localizedString('intent_detail'),
            path: ':intentId',
            component: () => import('@page/intent/detail'),
            children: [
              {
                name: localizedString('intent_attribute_details'),
                path: 'slot/:slotId',
                component: () =>
                  import('@page/action-parameter/intent-action-parameter')
              },
              {
                name: 'function',
                path: 'function/:functionId',
                component: () =>
                  import('@component/data-query/function-detail')
              },
              {
                name: 'agent-response',
                path: 'response/:responseId',
                // hidden: true,
                component: () => import('@page/intent/agent-response')
              },
              {
                name: 'state-update',
                path: 'state_update/:stateUpdateId',
                component: () => import('@component/state-update/state-update-detail')
              }
            ]
          }
        ]
      },
      {
        name: localizedString('dialog_act'),
        path: 'dialog_act',
        icon: <i className="iconfont icon-Intents_ic" />,
        component: () => import('@page/intent'),
        children: [
          {
            name: localizedString('intent_detail'),
            path: ':intentId',
            component: () => import('@page/intent/detail'),
            children: [
              {
                name: localizedString('intent_attribute_details'),
                path: 'slot/:slotId',
                component: () =>
                  import('@page/action-parameter/intent-action-parameter')
              },
              {
                name: 'agent-response',
                path: 'response/:responseId',
                // hidden: true,
                component: () => import('@page/intent/agent-response')
              },
              {
                name: 'state-update',
                path: 'state_update/:stateUpdateId',
                component: () => import('@component/state-update/state-update-detail')
              }
            ]
          }
        ]
      },
      {
        name: localizedString('sidebar_composite'),
        path: 'frame',
        icon: <i className="iconfont icon-shuxing_ic" />,
        component: () => import('@page/composite-attr/index'),
        children: [
          {
            name: localizedString('composite_detail'),
            path: ':frameId',
            component: () => import('@page/composite-attr/detail'),
            children: [
              {
                name: localizedString('composite_attribute_detail'),
                path: 'slot/:slotId',
                component: () =>
                  import('@page/action-parameter/composite-action-parameter')
              },
              {
                name: 'function',
                path: 'function/:functionId',
                component: () =>
                  import('@component/data-query/function-detail')
              },
              {
                name: 'state-update',
                path: 'state_update/:stateUpdateId',
                component: () => import('@component/state-update/state-update-detail')
              }
            ]
          }
        ]
      },
      {
        name: localizedString('sidebar_entity'),
        path: 'entity',
        icon: <i className="iconfont icon-Entities_ic" />,
        component: () => import('@page/entity'),
        children: [
          {
            name: localizedString('entity_detail'),
            path: ':entityId',
            component: () => import('@page/entity/detail'),
            children: [{
              name: 'function',
              path: 'function/:functionId',
              component: () =>
                import('@component/data-query/function-detail')
            }]
          }
        ]
      },
      {
        name: localizedString('knowledge'),
        path: 'knowledge_system_prompt',
        icon: <i className="iconfont icon-Entities_ic" />,
        component: () => import('@page/agent/knowledge-system-prompt'),
        children: [
          {
            name: localizedString('knowledge_detail'),
            path: ':knowledgeId',
            component: () => import('@page/knowledge/detail')
          }
        ]
      },
      {
        name: localizedString('knowledge'),
        path: 'knowledge',
        icon: <i className="iconfont icon-Entities_ic" />,
        component: () => import('@page/knowledge'),
        children: [
          {
            name: localizedString('knowledge_detail'),
            path: ':knowledgeId',
            component: () => import('@page/knowledge/detail')
          }
        ]
      },
      {
        name: localizedString('sidebar_service'),
        path: 'service',
        icon: <i className="iconfont icon-shuxing_ic" />,
        component: () => import('@page/service/index'),
        children: [
          {
            name: localizedString('service_detail'),
            path: ':serviceId',
            component: () => import('@page/service/detail'),
            children: [
              {
                name: localizedString('composite_attribute_detail'),
                path: 'slot/:slotId',
                component: () =>
                  import('@page/action-parameter/service-action-parameter')
              },
              {
                name: 'function',
                path: 'function/:functionId',
                component: () =>
                  import('@component/data-query/function-detail')
              }
            ]
          }
        ]
      },
      {
        name: localizedString('test_case'),
        path: 'test_case',
        icon: <FileDoneOutlined />,
        component: () => import('@page/test-case')
      },
      {
        name: localizedString('sidebar_agent_config'),
        path: 'setting',
        component: () => import('@page/agent/detail')
      },
      {
        name: localizedString('sidebar_agent_config'),
        path: 'debug_setting',
        component: () => import('@page/agent/detail')
      },
      {
        name: localizedString('sidebar_agent_config'),
        path: 'operate_setting',
        component: () => import('@page/agent/detail')
      },
      {
        name: 'Debug',
        path: 'debug',
        component: () => import('@page/agent/debug')
      },
      {
        name: 'Deploy',
        path: 'deploy',
        component: () => import('@page/agent/deploy')
      },
      {
        name: localizedString('sidebar_action_log'),
        path: 'version',
        icon: <i className="iconfont icon-Intents_ic" />,
        component: () => import('@page/agent/version')
      },
      {
        name: localizedString('outbound'),
        path: 'outbound',
        component: () => import('@page/outbound')
      }
    ]
  }
];


export const Routes: MenuDataInfo[] = [

  {
    name: localizedString('org'),
    path: 'org',
    icon: <i className="iconfont icon-agentbar" />,
    component: () => import('@page/org'),
    children: [
      {
        name: localizedString('org_create'),
        path: 'add',
        icon: <SolutionOutlined />,
        component: () => import('@page/org/add-org')
      },
      {
        name: localizedString('org_detail'),
        path: ':orgId/member',
        icon: <SolutionOutlined />,
        component: () => import('@page/org/member'),
      },
      {
        name: localizedString('org_detail'),
        path: ':orgId',
        icon: <SolutionOutlined />,
        component: () => import('@page/org/setting'),
        children: [
          {
            name: localizedString('org_setting_billing', undefined, 'Billing and plans'),
            path: 'billing',
            icon: <CreditCardOutlined />,
            component: () => import('@page/org/setting'),
          },
          {
            name: localizedString('org_setting_copilot', undefined, 'Copilot'),
            path: 'copilot',
            icon: <SolutionOutlined />,
            component: () => import('@page/org/setting'),
          },
          {
            name: localizedString('agent'),
            path: 'agent',
            icon: <i className="iconfont icon-agentbar" />,
            component: () => import('@page/agent'),
            children: agentChildren
          },
          {
            name: localizedString('sidebar_service_agents'),
            path: 'component_agent',
            icon: <i className="iconfont icon-agentbar" />,
            component: () => import('@page/agent')
          },
          {
            name: localizedString('sidebar_service_agents'),
            path: 'chatbot_agent',
            icon: <i className="iconfont icon-agentbar" />,
            component: () => import('@page/agent')
          },
          {
            name: localizedString('sidebar_service_agents'),
            path: 'backend_agent',
            icon: <i className="iconfont icon-agentbar" />,
            component: () => import('@page/agent')
          }
        ]
      }
    ]
  },
  {
    name: 'Third Party',
    path: 'third_party',
    icon: <CopyOutlined />,
    component: () => import('@page/agent/cloneable')
  },
  {
    name: localizedString('sidebar_account'),
    path: 'account',
    icon: <SolutionOutlined />,
    component: () => import('@page/account/settings/index')
  },
  {
    name: 'Copilot',
    path: 'copilot',
    icon: <SolutionOutlined />,
    component: () => import('@component/config-bot/index'),
  }
];
